import { usePortalScreen } from '../../app/telemetry/usePortalTelemetry';
import { portalScreenContext, trackPortalEvent, portalAction } from '../../app/telemetry/portalTelemetry';
import { Box, Button, Grid, Paper } from "@mui/material";
import AppTextInput from "../../app/components/AppTextInput";
import { FormProvider, useForm } from "react-hook-form";
import AppAutocomplete from "../../app/components/AppAutocomplete";
import EventsFormModel from "../../app/models/events/eventsFormModel";
import { yupResolver } from "@hookform/resolvers/yup";
import { SponsorshipValidationSchemas } from "./common/eventApprovalValidationSchemas";
import { useAppDispatch, useAppSelector } from "../../app/store/configureStore";
import { MapEventSponsorshipsToAutocompleteItems } from "../../app/utils/convertStaticDataToAutocompleteItems";
import { toast } from "react-toastify";
import { updatePcsEventAsync, gePcsEventByIdAsync } from "../../app/slices/pcsEventSlice";
import { API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_NEXT, MESSAGE_PROCESSING_DETAILS, MESSAGE_SAVE_AND_CONTINUE } from "../../app/utils/constant";
import { ApiRequestStatus } from "../../app/enums/apiRequestStatus";
import LoadingComponent from "../../app/layout/LoadingComponent";
import { PcsEventDto } from "../../app/models/events/pcsEventDto";
import { useEffect, useState } from "react";
import AppTextareaInput from "../../app/components/AppTextareaInput";
import { EventSponsorshipEnum } from "./common/eventSponsorshipEnum";
import AppRadioButtonsGroup from "../../app/components/AppRadioButtonsGroup";
import { yesNoArray } from "../../app/utils/util";
import { YesNoEnum } from "../../app/enums/yesNoEnum";

const { EventSponsored, SponsorshipType, RelationshipBetweenSponsorsAndTheSpeakers,
    RegistrationFeeForAttendees, AmountForRegistrationFeeForAttendees } = EventsFormModel.sponsorshipFormField;

interface Props {
    isSpaEditabe: boolean;
    handleNext: () => void;
    handleBack: () => void;
}

export default function Sponsorship({ isSpaEditabe, handleNext, handleBack }: Props) {
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(SponsorshipValidationSchemas)
    });
    const formObj = methods.watch();
    const { control, reset, trigger, getValues, formState: { isDirty } } = methods;

    const { eventSponsorships } = useAppSelector(state => state.ReferenceData);
    const { pcsEventDetails, getPcsEventStatus, updatePcsEventStatus } = useAppSelector(state => state.pcsEvent);
    const [eventId,] = useState(new URLSearchParams(window.location.search).get("eventId"));

    useEffect(() => {
        if (pcsEventDetails !== null && eventId !== null && eventId !== '') {
            reset({
                [EventSponsored.name]: pcsEventDetails?.eventSponsorshipId ?? '',
                [SponsorshipType.name]: pcsEventDetails?.sponsorshipType ?? '',
                [RelationshipBetweenSponsorsAndTheSpeakers.name]: pcsEventDetails?.sponsorshipSpeakerRelationship ?? '',
                [RegistrationFeeForAttendees.name]: pcsEventDetails?.hasRegistrationFee === true ? YesNoEnum.YES : YesNoEnum.NO,
                [AmountForRegistrationFeeForAttendees.name]: pcsEventDetails?.registrationFeeDetails
            });
        }
    }, [reset, pcsEventDetails, eventId]);

    const MapToPcsEvent = (values: any) => {
        const sponsorshipType = values.EventSponsored === EventSponsorshipEnum.EventNotSponsored
            ? '' : values.SponsorshipType;
        const sponsorshipValue = values.EventSponsored === EventSponsorshipEnum.EventNotSponsored
            ? '' : values.TotalValueOfSponsorshipGrant;
        const sponsorshipSpeakerRelationship = values.EventSponsored === EventSponsorshipEnum.EventNotSponsored
            ? '' : values.RelationshipBetweenSponsorsAndTheSpeakers;
        const hasRegistrationFee = values.RegistrationFeeForAttendees === YesNoEnum.YES;

        const pcsEventDtoObj: PcsEventDto = {
            ...pcsEventDetails,
            eventSponsorshipId: values.EventSponsored,
            sponsorshipType: sponsorshipType,
            sponsorshipValue: sponsorshipValue,
            sponsorshipSpeakerRelationship: sponsorshipSpeakerRelationship,
            hasRegistrationFee: hasRegistrationFee,
            registrationFeeDetails: hasRegistrationFee === false ? '' : values.AmountForRegistrationFeeForAttendees
        }
        return pcsEventDtoObj;
    }

    const dispatch = useAppDispatch();

    const saveDetails = async () => {
        const isStepValid = await trigger();
        if (!isStepValid) trackPortalEvent(portalScreenContext('Sponsorship'), 'ValidationBlocked', { step: 'sponsorship' });
        if (isStepValid && isDirty === true) {
            dispatch(updatePcsEventAsync(MapToPcsEvent(formObj))).then((response) => {
                if (response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED) {
                    pcsEventDetails?.eventId && dispatch(gePcsEventByIdAsync(pcsEventDetails?.eventId));
                    toast.success(MESSAGE_DETAILS_SAVED);
                    handleNext();
                }
            });
        }

        if (isStepValid && isDirty === false) {
            handleNext();
        }
    }

    usePortalScreen(portalScreenContext('Sponsorship'), undefined, !(getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending));

    if (getPcsEventStatus === ApiRequestStatus.Pending || updatePcsEventStatus === ApiRequestStatus.Pending) {
        return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
    }

    return <FormProvider {...methods}>
        <Box sx={{ minWidth: "100%" }} component={Paper}>
            <Grid container rowSpacing={4} sx={{ ml: '2%' }}>
                <Grid size={{ xs: 12 }}></Grid>

                <Grid size={{ xs: 12 }}>
                    <AppAutocomplete control={control} name={EventSponsored.name} label={EventSponsored.label}
                        items={MapEventSponsorshipsToAutocompleteItems(eventSponsorships ?? [])} disabled={isSpaEditabe} />
                </Grid>

                {getValues(EventSponsored.name as keyof PcsEventDto) !== EventSponsorshipEnum.EventNotSponsored && <>
                    <Grid size={{ xs: 12 }}>
                        <AppTextareaInput control={control} name={SponsorshipType.name} label={SponsorshipType.label}
                            maxStringCount={SponsorshipType.maxStringCount} disabled={isSpaEditabe} />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={RelationshipBetweenSponsorsAndTheSpeakers.name}
                            maxStringCount={RelationshipBetweenSponsorsAndTheSpeakers.maxStringCount}
                            label={RelationshipBetweenSponsorsAndTheSpeakers.label} disabled={isSpaEditabe} />
                    </Grid>
                </>}

                <Grid size={{ xs: 12 }}>
                    <AppRadioButtonsGroup control={control} name={RegistrationFeeForAttendees.name}
                        label={RegistrationFeeForAttendees.label} items={yesNoArray} disabled={isSpaEditabe} />
                </Grid>

                {getValues(RegistrationFeeForAttendees.name as keyof PcsEventDto)?.toUpperCase() === YesNoEnum.YES &&
                    <Grid size={{ xs: 12 }}>
                        <AppTextInput control={control} name={AmountForRegistrationFeeForAttendees.name}
                            maxStringCount={AmountForRegistrationFeeForAttendees.maxStringCount}
                            label={AmountForRegistrationFeeForAttendees.label} disabled={isSpaEditabe} />
                    </Grid>
                }

                <Grid size={{ xs: 5 }} sx={{ textAlign: 'start' }}>
                    <Button variant="outlined" color="primary" onClick={portalAction(portalScreenContext('Sponsorship'), 'Sponsorship.onClick.handleBack', handleBack)}>
                        Back to previous step
                    </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: 'end' }}>
                    <Button variant="contained" color="primary" onClick={saveDetails}>
                        {isDirty === true ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT}
                    </Button>
                </Grid>

                <Grid size={{ xs: 1 }}></Grid>

                <Grid size={{ xs: 12 }}></Grid>
            </Grid>
        </Box>
    </FormProvider>
}
